import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Cloud,
  CloudRain,
  CloudSun,
  Droplets,
  Leaf,
  LoaderCircle,
  RefreshCw,
  Share2,
  Sun,
  TreePine,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { MONITORING } from "@/constants/testIds";
import { MONITORING_MOCK } from "./data";
import {
  useDayData,
  useLifetime,
  useMonitoringSummary,
  useMonthData,
  usePlants,
  useSaveTariff,
  useYearData,
} from "./hooks";
import { useSite } from "@/contexts/SiteContext";
import {
  CO2_KG_PER_KWH,
  getCleaningNudge,
  getTomorrowForecast,
  fmtDate,
  fmtRs,
  INSTALL_DATE,
  MONTH_LONG,
  MONTH_SHORT,
  SYSTEM_COST_RS,
  SYSTEM_KWP,
  TARIFF_RS_PER_KWH,
} from "./mockData";
import { KG_CO2_PER_TREE } from "./constants";
import { downloadReport } from "./report";
import { downloadShareCard } from "./shareCard";
import PeriodPicker from "./PeriodPicker";
import SettingsPanel from "./SettingsPanel";
import MonitoringPasswordDialog, { monitoringProviderLabel } from "./MonitoringPasswordDialog";

const TARIFF_KEY = "greentek-tariff";

// Greentek Alliance · Solar Monitor - monitoring section.
// Ported from solar-dashboard/src/pages/Home.tsx: same views (day / month /
// year, hero power, curves, savings, PDF reports, share cards, settings) rendered inside the Alliance shell. Data comes from the
// react-query hooks in ./hooks (backend /api/monitoring/* or the local mock).
// Own chrome from the standalone app (fixed bottom tab bar) is replaced by an
// in-content Solar/Settings switch; navigation lives in the AppShell.

const INK = "#0d201d";
const MUTED = "#5a6e6a";
const FAINT = "#71807d";
const GOLD = "#c9a460";
const DEEP_GOLD = "#8a6826";
const PALE_GOLD = "#f4ead4";
const TEAL = "#237a6e";
const BRIGHT_TEAL = "#16b8a8";
const PALE_TEAL = "#e2eeed";
const BORDER = "#e8e4d8";
const DISPLAY = "'Bricolage Grotesque', Inter, sans-serif";

// PR banding - teal for on-track, amber scale for below-expected; never red
// (a cloudy day is not the plant's fault).
const MODE_ORDER = { day: 0, month: 1, year: 2 };
const NO_PLANT_DETAIL = "No plant linked to this account";

const isNoPlantLinkedError = (error) =>
  error?.response?.status === 404 &&
  error?.response?.data?.detail === NO_PLANT_DETAIL;

const PR_BANDS = {
  "On track": { accent: TEAL, bg: PALE_TEAL },
  "A bit low": { accent: DEEP_GOLD, bg: PALE_GOLD },
  "Well below expected": { accent: "#6b4a16", bg: PALE_GOLD },
};

// Builds the day-curve path from N evenly spaced slots (24 hourly, or
// 288/96/48 hi-res tiers). X maps 00:00–24:00 linearly, each slot drawn at
// its bucket center; nearly straight segments between points (heavy
// smoothing invents bumps the data never had), with a ~3-minute straight
// ramp down at the "now" cutoff.
function buildCurvePath(curve, nowH, width, height, max) {
  const EPS = 0.004; // tiny offset anchoring the pre-drop value
  const RAMP = 0.05; // ~3 minutes of ramp at the cutoff
  const N = curve.length;
  const pph = N / 24; // slots per hour (1 hourly, 12 native 5-min)
  const clampH = (h) => Math.max(0, Math.min(24, h));

  // Slot value at fractional hour h (exact at slot centers, nearest
  // otherwise - ramps only need an approximate anchor).
  const rawVal = (h) =>
    curve[Math.max(0, Math.min(N - 1, Math.round(h * pph - 0.5)))] ?? 0;
  const valAt = (h) => {
    if (nowH != null && h >= nowH) return 0;
    return rawVal(h);
  };

  // Point set: slot centers + ramp pair at the cutoff
  const hs = new Set();
  for (let i = 0; i < N; i++) hs.add((i + 0.5) / pph);
  if (nowH != null) {
    hs.add(clampH(nowH - EPS)); // last generated value
    hs.add(clampH(nowH + RAMP)); // bottom of the now cutoff
  }

  const pts = [...hs]
    .sort((a, b) => a - b)
    .map((h) => ({
      x: (h / 24) * width,
      y: height - (valAt(h) / max) * height,
    }));

  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    if (p1.x - p0.x < 2) {
      d += ` L ${p1.x},${p1.y}`; // ramp/cliff - straight line, no smoothing
    } else {
      const dx = p1.x - p0.x;
      d += ` C ${p0.x + dx * 0.15},${p0.y} ${p1.x - dx * 0.15},${p1.y} ${p1.x},${p1.y}`;
    }
  }
  return d;
}

// "9:12 – 10:06 AM" style time ranges
function fmtClock(h, withPeriod) {
  const hh = Math.floor(h) % 24;
  const mm = Math.min(59, Math.round((h - Math.floor(h)) * 60));
  const period = hh < 12 ? "AM" : "PM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  const base = `${h12}:${String(mm).padStart(2, "0")}`;
  return withPeriod ? `${base} ${period}` : base;
}

function fmtTimeRange(startH, endH) {
  const samePeriod = Math.floor(startH) < 12 === Math.floor(endH) < 12;
  return samePeriod
    ? `${fmtClock(startH, false)} – ${fmtClock(endH, true)}`
    : `${fmtClock(startH, true)} – ${fmtClock(endH, true)}`;
}

// Day power chart (desktop-informed, mockup B+C): actual curve as a thin
// gold line, today's weather-adjusted expectation as a dashed overlay (only
// present for the current day), readable kW axis with gridlines, and a hover
// tooltip. The SVG draws the plot; axis labels and the tooltip are HTML so
// their font size stays constant when the chart stretches on desktop.
// Light 3-slot moving average for the RENDERED hi-res path only - takes the
// jitter out of 5-min cloud-edge data without moving peaks or timing.
// Tooltips and markers keep the raw values.
function smooth3(arr) {
  return arr.map((v, i) => {
    const prev = arr[Math.max(0, i - 1)];
    const next = arr[Math.min(arr.length - 1, i + 1)];
    return Math.round(((prev + v + next) / 3) * 1000) / 1000;
  });
}

function DayCurveChart({ curve, expectedCurve, curveHiRes, nowH }) {
  const [hoverH, setHoverH] = useState(null);
  const W = 320,
    H = 88;
  const expected = expectedCurve ?? [];
  // Stored hi-res curve (5/15/30-min tiers) when the day has one; hourly
  // otherwise. N slots per day, drawn at bucket centers.
  const rawSeries = curveHiRes?.kw?.length ? curveHiRes.kw : curve;
  const pph = rawSeries.length / 24;
  const series = rawSeries.length > 24 ? smooth3(rawSeries) : rawSeries;
  const maxCurve =
    Math.max(Math.max(...rawSeries), Math.max(...expected, 0)) * 1.15 || 1;
  const line = buildCurvePath(series, nowH, W, H, maxCurve);
  const expectedLine = expected.length
    ? buildCurvePath(expected, null, W, H, maxCurve)
    : "";
  const nowX = Math.min(((nowH ?? 0) / 24) * W, W);
  const nowY =
    H -
    ((series[Math.min(Math.floor((nowH ?? 0) * pph), series.length - 1)] ?? 0) /
      maxCurve) *
      H;

  // Axis ticks at 0 / half / full scale; HTML labels positioned to match the
  // SVG plot box (plot height is H of the H+6 viewBox).
  const yPct = (v) => (1 - v / maxCurve) * (H / (H + 6)) * 100;
  const ticks = [maxCurve, maxCurve / 2, 0];
  const fmtKw = (v) => (v >= 10 ? Math.round(v) : Math.round(v * 10) / 10);

  const slotVal = (h) =>
    rawSeries[Math.max(0, Math.min(rawSeries.length - 1, Math.round(h * pph - 0.5)))] ?? 0;
  // Marker y rides the RENDERED (smoothed) line; the tooltip reads raw.
  const slotSmooth = (h) =>
    series[Math.max(0, Math.min(series.length - 1, Math.round(h * pph - 0.5)))] ?? 0;

  function handleMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = ((e.clientX - rect.left) / rect.width) * 24;
    // Snap to the nearest slot center (hour → hourly, 5-min → 5-min).
    const snapped = (Math.round(raw * pph - 0.5) + 0.5) / pph;
    setHoverH(Math.max(0.5 / pph, Math.min(24 - 0.5 / pph, snapped)));
  }

  const hoverXPct = hoverH != null ? (hoverH / 24) * 100 : 0;
  const tooltipLeft = Math.max(14, Math.min(86, hoverXPct));

  return (
    <div>
      <div className="mt-3 flex gap-1.5">
        {/* kW axis */}
        <div className="relative w-7 shrink-0" aria-hidden="true">
          {ticks.map((v, i) => (
            <span
              key={i}
              className="absolute right-0 text-[11px] leading-none [font-variant-numeric:tabular-nums]"
              style={{ color: FAINT, top: `${yPct(v)}%`, transform: "translateY(-50%)" }}
            >
              {fmtKw(v)}
            </span>
          ))}
          <span
            className="absolute right-0 text-[11px] leading-none"
            style={{ color: FAINT, top: "100%", transform: "translateY(2px)" }}
          >
            kW
          </span>
        </div>

        {/* plot */}
        <div className="relative flex-1">
          <svg viewBox={`0 0 ${W} ${H + 6}`} className="block w-full">
            <defs>
              <linearGradient id="goldfill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GOLD} stopOpacity="0.30" />
                <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
              </linearGradient>
            </defs>
            {ticks.map((v, i) => (
              <line
                key={i}
                x1="0"
                y1={(1 - v / maxCurve) * H}
                x2={W}
                y2={(1 - v / maxCurve) * H}
                stroke="#efece2"
                strokeWidth="1"
              />
            ))}
            {expectedLine && (
              <path
                d={expectedLine}
                fill="none"
                stroke={FAINT}
                strokeWidth="1.1"
                strokeDasharray="4 3"
              />
            )}
            <path d={`${line} L ${W},${H} L 0,${H} Z`} fill="url(#goldfill)" />
            <path
              d={line}
              fill="none"
              stroke={GOLD}
              strokeWidth="1"
              strokeLinecap="round"
            />
            {nowH != null && (
              <>
                <line
                  x1={nowX}
                  y1="4"
                  x2={nowX}
                  y2={H}
                  stroke="#d8d3c4"
                  strokeDasharray="3 3"
                  strokeWidth="1"
                />
                <circle
                  cx={nowX}
                  cy={nowY}
                  r="3"
                  fill={GOLD}
                  stroke="#fff"
                  strokeWidth="1.5"
                />
              </>
            )}
          </svg>

          {/* hover layer */}
          <div
            className="absolute inset-0 cursor-crosshair"
            onMouseMove={handleMove}
            onMouseLeave={() => setHoverH(null)}
          />
          {hoverH != null && (
            <>
              <div
                className="pointer-events-none absolute top-0 w-px"
                style={{
                  left: `${hoverXPct}%`,
                  height: `${(H / (H + 6)) * 100}%`,
                  background: "#d8d3c4",
                }}
              />
              <div
                className="pointer-events-none absolute h-2 w-2 rounded-full border border-white"
                style={{
                  left: `${hoverXPct}%`,
                  top: `${yPct(slotSmooth(hoverH))}%`,
                  transform: "translate(-50%, -50%)",
                  background: GOLD,
                }}
              />
              <div
                className="pointer-events-none absolute z-10 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[11px] shadow-[0_4px_16px_rgba(13,32,29,0.14)] [font-variant-numeric:tabular-nums]"
                style={{
                  left: `${tooltipLeft}%`,
                  top: 0,
                  transform: "translate(-50%, -110%)",
                  background: "#fff",
                  color: INK,
                }}
              >
                <span className="font-semibold">
                  {pph === 1
                    ? fmtTimeRange(Math.floor(hoverH), Math.floor(hoverH) + 1)
                    : fmtClock(hoverH, true)}
                </span>
                <span style={{ color: MUTED }}>
                  {" "}
                  · {fmtKw(slotVal(hoverH))} kW
                  {expected.length > 0 &&
                    ` · exp ${fmtKw(expected[Math.min(Math.floor(hoverH), 23)] ?? 0)}`}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="relative mt-1 ml-8 h-4 text-[11px]" style={{ color: FAINT }}>
        {[
          ["6 AM", 25],
          ["12 PM", 50],
          ["6 PM", 75],
        ].map(([label, pct]) => (
          <span
            key={label}
            className="absolute"
            style={{ left: `${pct}%`, transform: "translateX(-50%)" }}
          >
            {label}
          </span>
        ))}
      </div>
      <div
        className="mt-2 flex items-center justify-center gap-4 text-[11px]"
        style={{ color: FAINT }}
      >
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-[2px] w-4 rounded-full"
            style={{ background: GOLD }}
          />
          Actual
        </span>
        {expected.length > 0 && (
          <span className="flex items-center gap-1.5">
            <svg width="16" height="2" aria-hidden="true">
              <line
                x1="0"
                y1="1"
                x2="16"
                y2="1"
                stroke={FAINT}
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
            </svg>
            Expected (today's weather)
          </span>
        )}
      </div>
    </div>
  );
}

function SunIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      className={className}
    >
      <circle cx="12" cy="12" r="4.2" fill="currentColor" stroke="none" />
      <path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4M5 5l1.7 1.7M17.3 17.3L19 19M19 5l-1.7 1.7M6.7 17.3L5 19" />
    </svg>
  );
}

function Card({ children, className = "", testId }) {
  return (
    <div
      data-testid={testId}
      className={`bg-white rounded-[18px] shadow-[0_2px_14px_rgba(13,32,29,0.05)] border p-5 ${className}`}
      style={{ borderColor: BORDER }}
    >
      {children}
    </div>
  );
}

function Label({ children }) {
  return (
    <p className="text-[12px] font-medium tracking-wide" style={{ color: MUTED }}>
      {children}
    </p>
  );
}

function ImpactInfoButton() {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={MONITORING.co2Info}
        onClick={() => setOpen(!open)}
        className="flex h-4 w-4 items-center justify-center rounded-full border text-[11px] font-bold"
        style={{ borderColor: FAINT, color: FAINT }}
        aria-label="About CO2 avoided and trees equivalent"
        aria-expanded={open}
      >
        i
      </button>
      {open && (
        <div
          className="fixed inset-x-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-50 rounded-[14px] border bg-white p-4 text-left shadow-[0_8px_30px_rgba(13,32,29,0.12)] sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-6 sm:w-72"
          style={{ borderColor: BORDER }}
        >
          <p className="text-[12px] leading-relaxed" style={{ color: MUTED }}>
            CO2 avoided is your generation in kWh multiplied by {CO2_KG_PER_KWH} kg, the
            emission factor of the Indian grid. Trees equivalent divides that CO2 by{" "}
            {KG_CO2_PER_TREE} kg, roughly what one tree absorbs in a year.
          </p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 text-[12px] font-semibold"
            style={{ color: DEEP_GOLD }}
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, unit, note, valueColor, testId, icon }) {
  return (
    <Card testId={testId}>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {icon}
      </div>
      <p
        className="mt-1 text-[28px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
        style={{ fontFamily: DISPLAY, color: valueColor ?? INK }}
      >
        {value}
        {unit && (
          <span className="text-[14px]" style={{ color: MUTED }}>
            {" "}
            {unit}
          </span>
        )}
      </p>
      <p className="text-[12px]" style={{ color: FAINT }}>
        {note}
      </p>
    </Card>
  );
}

// Investment payback tracker - used by both the day view (live ops) and the
// year view (the money story), so it's a component rather than two copies.
function PaybackCard({ payback, systemCostRs }) {
  // Cost 0/unset = "not tracked": grey the card out and point the owner at
  // Settings instead of showing a meaningless 100%/∞ progress bar.
  if (!(systemCostRs > 0)) {
    return (
      <Card testId="payback-card" className="opacity-75">
        <Label>Investment payback</Label>
        <p
          className="mt-2 text-[14px] leading-snug"
          style={{ color: FAINT }}
          data-testid="payback-empty"
        >
          Add your system cost in Settings to track your payback progress.
        </p>
        <div
          className="mt-3 h-2 overflow-hidden rounded-full"
          style={{ background: BORDER }}
        />
      </Card>
    );
  }
  return (
    <Card testId="payback-card">
      <Label>Investment payback</Label>
      <p
        className="mt-1 text-[26px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
        style={{ fontFamily: DISPLAY, color: INK }}
      >
        {fmtRs(payback.recovered)} recovered
      </p>
      <p
        className="text-[12px] [font-variant-numeric:tabular-nums]"
        style={{ color: FAINT }}
      >
        {payback.pct}% of {fmtRs(systemCostRs)} · break-even ≈{" "}
        {MONTH_LONG[payback.breakeven.getMonth()]} {payback.breakeven.getFullYear()}
      </p>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full"
        style={{ background: PALE_TEAL }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${payback.pct}%`,
            background: `linear-gradient(90deg, ${GOLD}, ${DEEP_GOLD})`,
          }}
        />
      </div>
    </Card>
  );
}

// Worst day of a month payload, derived from days[] - the payload contract
// only carries bestDay, so the low end is computed client-side.
const worstDayOf = (days) =>
  days?.length ? days.reduce((a, b) => (b.kwh < a.kwh ? b : a)) : null;

function Bars({ values, highlight, labels }) {
  const max = Math.max(...values, 0.001);
  return (
    <div className={`mt-4 flex h-24 items-end gap-[3px] ${labels ? "justify-between" : ""}`}>
      {values.map((v, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1.5 min-w-0">
          <div
            className="w-full rounded-full"
            style={{
              height: `${Math.max((v / max) * 84, 3)}px`,
              background: i === highlight ? GOLD : PALE_TEAL,
            }}
          />
          {labels && (
            <span
              className="text-[11px] truncate"
              style={{
                color: i === highlight ? INK : FAINT,
                fontWeight: i === highlight ? 600 : 400,
              }}
            >
              {labels[i]}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// "2:05 PM" from an ISO timestamp (sample times for held/offline power)
function fmtClockIso(iso) {
  const at = new Date(iso);
  const hh = at.getHours() % 12 === 0 ? 12 : at.getHours() % 12;
  const mm = String(at.getMinutes()).padStart(2, "0");
  return `${hh}:${mm} ${at.getHours() < 12 ? "AM" : "PM"}`;
}

// "Last updated 2:05 PM" - live mode only; the mock says what it is instead
// of faking freshness.
function updatedLabel(summary) {
  if (MONITORING_MOCK) return "Demo data · mock source";
  if (!summary?.as_of) return summary?.stale ? "Feed unavailable" : "No live reading yet";
  const at = new Date(summary.as_of);
  const hh = at.getHours() % 12 === 0 ? 12 : at.getHours() % 12;
  const mm = String(at.getMinutes()).padStart(2, "0");
  return `Last updated ${hh}:${mm} ${at.getHours() < 12 ? "AM" : "PM"}`;
}

export function MonitoringSection({ role } = {}) {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [mode, setMode] = useState("day");
  const [anchor, setAnchor] = useState(today);
  // Page-transition direction: 1 = forward (slides in from the right),
  // -1 = back (from the left). Consumed by the period-page wrapper below.
  const [navDir, setNavDir] = useState(1);
  const [tipOpen, setTipOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [tab, setTab] = useState("solar");
  // Admin selection remains deliberately separate from the member site context.
  const [adminPlantId, setAdminPlantId] = useState(null);
  const {
    selectedSiteId,
    selectedSite,
    sites = [],
    setSelectedSiteId,
    isLoading: sitesLoading,
    error: sitesError,
    refetch: refetchSites,
  } = useSite();
  const [tariff, setTariff] = useState(() => {
    if (!MONITORING_MOCK) return TARIFF_RS_PER_KWH; // live: server value syncs below
    const saved = parseFloat(localStorage.getItem(TARIFF_KEY) ?? "");
    return !isNaN(saved) && saved > 0 ? saved : TARIFF_RS_PER_KWH;
  });

  const isAdmin = role === "admin";
  const isViewer = selectedSite?.access === "viewer";
  const showPlantSelector = isAdmin && !MONITORING_MOCK;
  const showSiteSelector = !isAdmin && !MONITORING_MOCK && sites.length > 1;
  const plantsQ = usePlants(showPlantSelector);
  const plants = plantsQ.data ?? [];
  const plantId = isAdmin ? adminPlantId : selectedSiteId;

  const summaryQ = useMonitoringSummary(plantId);
  const dayQ = useDayData(anchor, tariff, plantId);
  const monthQ = useMonthData(anchor.getFullYear(), anchor.getMonth(), tariff, plantId);
  // Previous month for the trends comparison - just another month query on a
  // shifted anchor, not a new endpoint.
  const prevAnchor = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
  const prevMonthQ = useMonthData(prevAnchor.getFullYear(), prevAnchor.getMonth(), tariff, plantId);
  const yearQ = useYearData(anchor.getFullYear(), tariff, plantId);
  const lifetimeQ = useLifetime(plantId);
  const saveTariffMutation = useSaveTariff(plantId);

  const summary = summaryQ.data;
  const monitoringAccount = summary?.account;
  const loginRejected = monitoringAccount?.state === "rejected";
  const canRepairMonitoringLogin =
    role === "customer" && !isViewer && monitoringAccount?.customer_managed === true;

  useEffect(() => {
    if (
      !loginRejected ||
      !canRepairMonitoringLogin ||
      !monitoringAccount?.id
    ) return;
    const key = `monitoring:password-prompt:${monitoringAccount.id}`;
    const rejection = monitoringAccount.credentials_rejected_at || "rejected";
    try {
      if (localStorage.getItem(key) === rejection) return;
      localStorage.setItem(key, rejection);
    } catch {
      // Storage can be unavailable in private browsing. The prompt still works for this render.
    }
    setPasswordDialogOpen(true);
  }, [
    role,
    loginRejected,
    canRepairMonitoringLogin,
    monitoringAccount?.id,
    monitoringAccount?.customer_managed,
    monitoringAccount?.credentials_rejected_at,
  ]);

  // Live mode: the tariff is server-persisted - adopt it once the summary lands.
  useEffect(() => {
    if (!MONITORING_MOCK && summary?.tariffRsPerKwh) setTariff(summary.tariffRsPerKwh);
  }, [summary]);

  async function updateTariff(t) {
    if (MONITORING_MOCK) {
      setTariff(t);
      localStorage.setItem(TARIFF_KEY, String(t));
      return;
    }
    await saveTariffMutation.mutateAsync(t);
    setTariff(t);
  }

  const day = dayQ.data;
  const month = monthQ.data;
  const prevMonth = prevMonthQ.data;
  const year = yearQ.data;
  const lifetime = lifetimeQ.data;

  // Live mode renders only a server forecast. A missing live forecast is an
  // explicit unavailable state; demo estimates stay confined to mock mode.
  const mockForecast = useMemo(() => getTomorrowForecast(), []);
  const serverForecast = MONITORING_MOCK ? null : summary?.forecast;
  const forecast = MONITORING_MOCK
    ? mockForecast
    : serverForecast
      ? {
        condition: serverForecast.condition,
        expectedKwh: serverForecast.tomorrowKwh,
        note: serverForecast.note,
      }
      : null;
  const forecastUnavailableCopy =
    MONITORING_MOCK
      ? "Today's weather comparison is not available in demo mode."
      : summary?.forecastStatus === "unavailable_no_coordinates"
      ? "Site coordinates are not available, so no weather-based estimate can be shown."
      : summary?.forecastStatus === "unavailable_upstream"
        ? "Weather forecast data is currently unavailable."
        : today.getHours() >= 19
          ? "Today's weather comparison is unavailable after sunset."
          : today.getHours() < 6
            ? "Today's weather comparison will appear after sunrise."
            : "Today's weather comparison is not available yet.";
  // Performance ratio - live only, only when the backend supplies it.
  const pr = MONITORING_MOCK ? null : summary?.pr;
  const prBand = pr ? (PR_BANDS[pr.label] ?? PR_BANDS["A bit low"]) : null;
  // Cleaning nudge: mock mode derives it from per-day history (the reference
  // app computed it the same way); live mode takes the backend's verdict.
  const mockCleaningNudge = useMemo(() => (MONITORING_MOCK ? getCleaningNudge() : null), []);
  const cleaningNudge = MONITORING_MOCK
    ? mockCleaningNudge
    : summary?.performance?.cleaningNudge
      ? true
      : null;

  const systemKwp = MONITORING_MOCK
    ? summary?.systemKwp ?? SYSTEM_KWP
    : summary?.systemKwp ?? 0;
  const installDate = MONITORING_MOCK
    ? summary?.installDate ?? INSTALL_DATE.toISOString()
    : summary?.installDate ?? null;
  const installDateObj = useMemo(
    () => (installDate ? new Date(installDate) : null),
    [installDate],
  );
  const city = MONITORING_MOCK ? summary?.city || "Rajkot" : summary?.city || "";
  const lifetimeKwh = lifetime?.kwh ?? 0;
  const lifetimeCo2Kg = lifetime?.co2Kg ?? 0;
  const lifetimeSource = lifetime?.source ?? "app";
  // Live mode never substitutes a demo cost. Mock mode keeps the deterministic
  // fallback used by the standalone preview.
  const systemCostRs = MONITORING_MOCK
    ? summary?.systemCostRs ?? lifetime?.systemCostRs ?? SYSTEM_COST_RS
    : summary?.systemCostRs ?? 0;

  const payback = useMemo(() => {
    // lifetimeKwh is the all-time total (already includes today), so recovered
    // derives from it directly - tariff-reactive, no double counting.
    const recovered = Math.round(lifetimeKwh * tariff);
    const pct =
      systemCostRs > 0
        ? Math.min(100, Math.max(0, Math.round((recovered / systemCostRs) * 100)))
        : 0;
    const elapsedMonths = Math.max(today.getMonth() + 1, 1);
    const ytdSaved = year?.savedRs ?? 0;
    const avgMonthly = Math.max(ytdSaved / elapsedMonths, 1);
    const monthsRemaining = Math.max(0, Math.ceil((systemCostRs - recovered) / avgMonthly));
    const breakeven = new Date(today.getFullYear(), today.getMonth() + monthsRemaining, 1);
    return { recovered, pct, breakeven };
  }, [today, tariff, lifetimeKwh, systemCostRs, year]);

  const isToday = anchor.toDateString() === today.toDateString();
  const isCurrentMonth =
    anchor.getFullYear() === today.getFullYear() && anchor.getMonth() === today.getMonth();
  const isCurrentYear = anchor.getFullYear() === today.getFullYear();
  const canGoNext = mode === "day" ? !isToday : mode === "month" ? !isCurrentMonth : !isCurrentYear;

  // The month comparison is hidden while the previous month predates install.
  const hasPrevMonth =
    !installDateObj ||
    prevAnchor >= new Date(installDateObj.getFullYear(), installDateObj.getMonth(), 1);
  // Month trends: the low end of the month, derived from days[] (the payload
  // only ships bestDay).
  const worstDay = mode === "month" && month ? worstDayOf(month.days) : null;

  function goTo(nextMode, nextAnchor, dir) {
    // Transition direction: explicit (‹ › nav), else mode order for tab
    // switches, else anchor comparison for calendar jumps.
    const fallback =
      nextMode !== mode
        ? Math.sign(MODE_ORDER[nextMode] - MODE_ORDER[mode])
        : Math.sign(nextAnchor - anchor);
    setNavDir(dir ?? (fallback || 1));
    setMode(nextMode);
    setAnchor(nextAnchor);
  }

  // Live hero figures (null in mock → demo values)
  const liveKw = summary?.currentKw ?? null;
  const kwHeld = summary?.currentKwHeld ?? false;
  const kwOffline = summary?.currentKwOffline ?? false;
  const displayHeld = kwHeld || summary?.stale === true;
  const liveReadingUnavailable = !MONITORING_MOCK && liveKw == null;
  const heroKw = kwOffline || liveReadingUnavailable ? "-" : (liveKw ?? 4.2);
  const heroPct =
    liveKw != null && systemKwp > 0
      ? Math.min(100, Math.round((liveKw / systemKwp) * 100))
      : MONITORING_MOCK && !kwOffline
        ? 84
        : 0;
  // Sample-and-hold display: a held/offline value is never "Producing now".
  // When a real kW reading exists, show it inline ("Producing now · 0.7 kW").
  const heroStateLabel = kwOffline
    ? "Not reporting"
    : liveReadingUnavailable
      ? "Live reading unavailable"
    : displayHeld
      ? liveKw != null
        ? `Last reported · ${liveKw} kW`
        : "Last reported"
      : liveKw != null
        ? `Producing now · ${liveKw} kW`
        : "Producing now";
  const statusInfo =
    !MONITORING_MOCK && summary?.stale
      ? { label: summary.as_of ? "Feed delayed" : "No live reading", ok: false }
      : summary
        ? { label: summary.statusLabel, ok: summary.statusOk }
        : { label: "All good", ok: true };

  const periodLabel =
    mode === "day"
      ? isToday
        ? "Today"
        : fmtDate(anchor)
      : mode === "month"
        ? `${MONTH_LONG[anchor.getMonth()]} ${anchor.getFullYear()}${isCurrentMonth ? " · so far" : ""}`
        : `${anchor.getFullYear()}${isCurrentYear ? " · so far" : ""}`;

  // Weather-adjusted performance verdict: the backend compares today's
  // generation against what the actual weather predicts, over the plant's
  // 30-day norm. state null = not enough data yet; the whole object null =
  // meteo unavailable - either way the chip falls back to plain text.
  const performance = summary?.performance ?? null;
  const perfState = isToday ? (performance?.state ?? null) : null;

  function shift(dir) {
    const d = new Date(anchor);
    if (mode === "day") d.setDate(d.getDate() + dir);
    else if (mode === "month") d.setMonth(d.getMonth() + dir, 1);
    else d.setFullYear(d.getFullYear() + dir, 0, 1);
    if (dir === 1) {
      const limit = new Date(today);
      if (mode === "month") limit.setDate(1);
      if (mode === "year") limit.setMonth(0, 1);
      if (d > limit) return;
    }
    if (
      dir === -1 &&
      installDateObj &&
      d < new Date(installDateObj.getFullYear(), installDateObj.getMonth(), 1)
    )
      return;
    goTo(mode, d, dir);
  }

  async function handleDownload() {
    const meta = {
      systemKwp,
      city,
      installDate,
      lifetimeKwh,
      lifetimeCo2Kg,
    };
    const report =
      mode === "day"
        ? { kind: "day", label: fmtDate(anchor), day }
        : mode === "month"
          ? {
              kind: "month",
              label: `${MONTH_LONG[anchor.getMonth()]} ${anchor.getFullYear()}`,
              year: anchor.getFullYear(),
              month: anchor.getMonth(),
              data: month,
            }
          : {
              kind: "year",
              label: `Year ${anchor.getFullYear()}`,
              year: anchor.getFullYear(),
              data: year,
            };
    await downloadReport(report, tariff, meta);
    toast.success("Report downloaded");
  }

  async function handleShare() {
    const monthLabel = `${MONTH_LONG[anchor.getMonth()]} ${anchor.getFullYear()}`;
    await downloadShareCard({
      monthLabel,
      kwh: month.kwh,
      savedRs: month.savedRs,
      co2Kg: month.co2Kg,
      percentile: month.percentile,
      trees: Math.round(month.co2Kg / KG_CO2_PER_TREE),
    });
    toast.success("Summary card downloaded");
  }

  const queries = [summaryQ, dayQ, monthQ, yearQ, lifetimeQ];
  const isLoading = queries.some((q) => q.isLoading);
  const failed = queries.find((q) => q.error) ?? (sitesError ? { error: sitesError } : null);
  const noPlantLinked =
    !MONITORING_MOCK &&
    role === "customer" &&
    ((!sitesLoading && !sitesError && sites.length === 0) ||
      isNoPlantLinkedError(sitesError) ||
      queries.some((query) => isNoPlantLinkedError(query.error)));
  const retry = () => {
    refetchSites?.();
    queries.forEach((q) => q.refetch?.());
  };
  const navBusy = dayQ.isFetching || monthQ.isFetching || yearQ.isFetching;

  // Stale marker from the active period's payload - never fake liveness.
  // A newly activated plant has no period snapshot yet, even when its live
  // summary is healthy. Treat that state as an initial sync, not an outage.
  const activePeriod = mode === "day" ? day : mode === "month" ? month : year;
  const activeStale = activePeriod?.stale;
  const freshnessNotice =
    loginRejected
      ? {
          kind: "login-rejected",
          copy: canRepairMonitoringLogin
            ? `Monitoring is paused: the ${monitoringProviderLabel(monitoringAccount.provider)} login was rejected. If you changed your portal password, enter the new one.`
            : `Monitoring is paused: the ${monitoringProviderLabel(monitoringAccount.provider)} login was rejected. Greentek has been notified.`,
        }
      : summary?.stale === true
      ? {
          kind: "unreachable",
          copy: summary?.as_of
            ? "The monitoring feed is unreachable - showing last known data."
            : "The monitoring feed is unreachable - no live reading is available yet.",
        }
      : activeStale && !activePeriod?.as_of
        ? {
            kind: "initial-sync",
            copy: "Live monitoring is connected. Waiting for the first stored sample.",
          }
        : activeStale
          ? {
              kind: "delayed",
              copy: "Monitoring data is delayed - showing the latest saved data.",
            }
          : null;
  const onboarding = summary?.onboarding;
  const historyLoading = ["queued", "daily_importing", "curve_importing"].includes(
    onboarding?.state
  );
  const importedDays = onboarding?.daily_days_written ?? 0;

  // "Now" marker hour for today's chart (DayCurveChart does the plotting)
  const nowHour = today.getHours() + today.getMinutes() / 60;

  return (
    <div className="antialiased" style={{ color: INK }}>
      {navBusy && (
        <div
          data-testid="nav-loading"
          className="fixed top-0 left-0 right-0 z-50 h-[3px]"
          style={{ background: `linear-gradient(90deg, ${GOLD}, ${DEEP_GOLD})` }}
        />
      )}
      <div className="mx-auto max-w-md space-y-4 md:max-w-3xl lg:max-w-6xl" data-testid="monitoring-section">
        {/* Header */}
        <header
          className="flex items-start justify-between px-1"
          data-testid="monitoring-selected-site"
          data-plant-id={selectedSiteId}
        >
          <div>
            <p
              className="text-[11px] font-semibold tracking-[0.18em] uppercase"
              style={{ color: DEEP_GOLD }}
            >
              Greentek Alliance
            </p>
            <h1
              className="text-[30px] font-semibold tracking-tight leading-tight mt-0.5"
              style={{ fontFamily: DISPLAY }}
            >
              {noPlantLinked ? "My Solar" : summary?.plantName || "My Solar"}
            </h1>
            {!noPlantLinked && (
              <p className="text-[13px]" style={{ color: MUTED }}>
                {fmtDate(today)}{city ? ` · ${city}` : ""}
              </p>
            )}
            {isViewer && selectedSite?.owner_name && (
              <p className="mt-1 text-[12px]" style={{ color: MUTED }}>
                Shared with you by {selectedSite.owner_name}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {!noPlantLinked && (
              <div
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 mt-2"
                style={{ background: statusInfo.ok ? PALE_TEAL : PALE_GOLD }}
                data-testid="status-pill"
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: statusInfo.ok ? BRIGHT_TEAL : GOLD }}
                />
                <span
                  className="text-[12px] font-semibold"
                  style={{ color: statusInfo.ok ? TEAL : DEEP_GOLD }}
                >
                  {statusInfo.label}
                </span>
              </div>
            )}
            {/* Admin-only plant switcher - members always see their own plant,
                and mock mode has a single demo plant, so neither gets this. */}
            {showPlantSelector && plants.length > 0 && (
              <select
                data-testid="plant-selector"
                value={plantId ?? summary?.plantId ?? plants[0].id}
                onChange={(e) => setAdminPlantId(e.target.value)}
                className="max-w-[60vw] truncate rounded-full border bg-white px-3 py-1.5 text-[12px] font-semibold sm:max-w-[240px]"
                style={{ borderColor: BORDER, color: INK }}
                aria-label="Select plant"
              >
                {plants.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.systemKwp} kW
                  </option>
                ))}
              </select>
            )}
            {showSiteSelector && (
              <select
                data-testid={MONITORING.siteSelector}
                value={selectedSiteId || sites[0].id}
                onChange={(event) => setSelectedSiteId(event.target.value)}
                className="max-w-[60vw] truncate rounded-full border bg-white px-3 py-1.5 text-[12px] font-semibold sm:max-w-[240px]"
                style={{ borderColor: BORDER, color: INK }}
                aria-label="Select site"
              >
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            )}
          </div>
        </header>

        {/* Solar / Settings switch (replaces the standalone app's bottom bar) */}
        {!noPlantLinked && (
          <div
            data-testid={MONITORING.tabStrip}
            className="flex items-center gap-1 rounded-full bg-white border p-1"
            style={{ borderColor: BORDER }}
          >
            {[
              { id: "solar", label: "Solar" },
              { id: "settings", label: "Settings" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className="flex-1 rounded-full py-2 text-[13px] font-semibold transition-[background-color,color] duration-150 active:scale-[0.98]"
                style={tab === t.id ? { background: INK, color: "#fff" } : { color: MUTED }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {noPlantLinked ? (
            <Card className="py-8 text-center" testId={MONITORING.emptyState}>
              <p
                className="text-[11px] font-semibold tracking-[0.18em]"
                style={{ color: DEEP_GOLD }}
              >
                MY SOLAR
              </p>
              <h2
                className="mt-2 text-[24px] font-semibold tracking-tight"
                style={{ color: INK, fontFamily: DISPLAY }}
              >
                Your solar site is not connected yet
              </h2>
              <p className="mx-auto mt-2 max-w-lg text-[13px]" style={{ color: MUTED }}>
                Greentek links your plant after commissioning. Until then you can refer friends
                and track your referrals here.
              </p>
              <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
                <button
                  data-testid={MONITORING.referButton}
                  onClick={() => navigate("/refer")}
                  className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-[13px] font-semibold text-white active:scale-[0.98] transition-transform duration-150"
                  style={{ background: INK }}
                >
                  Refer a friend
                </button>
                <button
                  data-testid={MONITORING.supportButton}
                  onClick={() => navigate("/support")}
                  className="inline-flex items-center justify-center rounded-full border px-5 py-2.5 text-[13px] font-semibold active:scale-[0.98] transition-transform duration-150"
                  style={{ borderColor: GOLD, color: DEEP_GOLD, background: "#fff" }}
                >
                  Contact support
                </button>
              </div>
            </Card>
        ) : tab === "solar" ? (
          sitesLoading || isLoading ? (
            <Card className="flex items-center justify-center gap-3 py-10" testId="monitoring-loading">
              <LoaderCircle className="animate-spin" size={20} style={{ color: MUTED }} />
              <p className="text-[13px]" style={{ color: MUTED }}>
                Loading your plant data…
              </p>
            </Card>
          ) : failed ? (
            <Card className="py-8 text-center" testId="monitoring-error">
              <p className="text-[14px] font-medium" style={{ color: INK }}>
                We couldn't load your plant data
              </p>
              <p className="mt-1 text-[12px]" style={{ color: FAINT }}>
                {failed.error?.response?.data?.detail || failed.error?.message}
              </p>
              <button
                data-testid="monitoring-retry"
                onClick={retry}
                className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white active:scale-[0.98] transition-transform duration-150"
                style={{ background: INK }}
              >
                <RefreshCw size={14} /> Try again
              </button>
            </Card>
          ) : (
            <>
              {/* Mode switch + period nav */}
              <div
                className="flex items-center gap-1 rounded-full bg-white border p-1"
                style={{ borderColor: BORDER }}
              >
                {["day", "month", "year"].map((m) => (
                  <button
                    key={m}
                    data-testid={`mode-${m}`}
                    onClick={() => goTo(m, anchor)}
                    className="flex-1 rounded-full py-2 text-[13px] font-semibold capitalize transition-[background-color,color] duration-150 active:scale-[0.98]"
                    style={mode === m ? { background: INK, color: "#fff" } : { color: MUTED }}
                  >
                    {m}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between px-1">
                <button
                  onClick={() => shift(-1)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white border text-[16px] active:scale-[0.98] transition-transform duration-150"
                  style={{ borderColor: BORDER, color: INK }}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  data-testid="period-picker-open"
                  onClick={() => setPickerOpen(true)}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{ background: "transparent" }}
                  aria-label="Choose period"
                >
                  <span className="text-[15px] font-semibold" style={{ fontFamily: DISPLAY }}>
                    {periodLabel}
                  </span>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={FAINT}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    className="h-3 w-3"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                <div className="flex items-center gap-2">
                  {!canGoNext ? null : (
                    <button
                      data-testid="period-jump-current"
                      onClick={() => {
                        if (mode === "day") goTo(mode, new Date(today));
                        else if (mode === "month")
                          goTo(mode, new Date(today.getFullYear(), today.getMonth(), 1));
                        else goTo(mode, new Date(today.getFullYear(), 0, 1));
                      }}
                      className="flex h-9 items-center rounded-full border px-3 text-[12px] font-semibold active:scale-[0.98] transition-transform duration-150"
                      style={{ borderColor: BORDER, color: TEAL, background: "#fff" }}
                    >
                      {mode === "day" ? "Today" : mode === "month" ? "This month" : "This year"}
                    </button>
                  )}
                  {mode !== "day" && (
                    <button
                      data-testid="report-download-button"
                      onClick={handleDownload}
                      className="flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-[12px] font-semibold active:scale-[0.98] transition-transform duration-150"
                      style={{ borderColor: GOLD, color: DEEP_GOLD, background: "#fff" }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className="h-3.5 w-3.5"
                      >
                        <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" />
                      </svg>
                      Report
                    </button>
                  )}
                  <button
                    onClick={() => shift(1)}
                    disabled={!canGoNext}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white border text-[16px] disabled:opacity-30 active:scale-[0.98] transition-transform duration-150"
                    style={{ borderColor: BORDER, color: INK }}
                    aria-label="Next"
                  >
                    ›
                  </button>
                </div>
              </div>

              {/* Stale-data notice (live mode; backend marks degraded responses) */}
              {freshnessNotice && (
                <div
                  data-testid="stale-data-note"
                  data-state={freshnessNotice.kind}
                  aria-live="polite"
                  className="flex items-center gap-2 rounded-[14px] px-4 py-3"
                  style={{ background: PALE_GOLD }}
                >
                  {freshnessNotice.kind === "initial-sync" ? (
                    <LoaderCircle
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 animate-spin"
                      style={{ color: DEEP_GOLD }}
                    />
                  ) : (
                    <AlertTriangle
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0"
                      style={{ color: DEEP_GOLD }}
                    />
                  )}
                  <p className="text-[12px]" style={{ color: DEEP_GOLD }}>
                    {freshnessNotice.copy}
                  </p>
                  {freshnessNotice.kind === "login-rejected" && canRepairMonitoringLogin && (
                    <button
                      type="button"
                      onClick={() => setPasswordDialogOpen(true)}
                      className="ml-auto shrink-0 rounded-full border border-current px-3 py-1.5 text-[12px] font-semibold"
                      style={{ color: DEEP_GOLD }}
                    >
                      Update login
                    </button>
                  )}
                </div>
              )}

              {canRepairMonitoringLogin && (
                <MonitoringPasswordDialog
                  account={monitoringAccount}
                  plantId={summary?.plantId || plantId}
                  open={passwordDialogOpen}
                  onOpenChange={setPasswordDialogOpen}
                  onSuccess={() => summaryQ.refetch?.()}
                />
              )}

              {historyLoading && (
                <div
                  data-testid="onboarding-progress-note"
                  aria-live="polite"
                  className="flex items-center gap-2 rounded-[14px] px-4 py-3"
                  style={{ background: PALE_TEAL }}
                >
                  <LoaderCircle
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 animate-spin"
                    style={{ color: TEAL }}
                  />
                  <p className="text-[12px]" style={{ color: TEAL }}>
                    {importedDays > 0
                      ? `${importedDays} days imported. Remaining history is still loading.`
                      : "Solar history is loading. Imported data will appear here as it arrives."}
                  </p>
                </div>
              )}

              {/* ── PERIOD PAGES ─ keying on mode+anchor replays the
                  directional slide/fade on every page change (CSS keyframes
                  page-in-right / page-in-left in index.css) ─────────────── */}
              <div
                key={`${mode}-${anchor.getTime()}`}
                className="space-y-4"
                style={{
                  animation: `${navDir === 1 ? "page-in-right" : "page-in-left"} 260ms cubic-bezier(0.32, 0.72, 0, 1)`,
                }}
              >
              {/* ── DAY VIEW ─────────────────────────────────────────── */}
              {mode === "day" && day && (
                <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
                  {isToday && (
                    <Card testId="live-hero-card" className="lg:col-span-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p
                            className="flex items-center gap-1.5 text-[12px] font-medium"
                            style={{ color: MUTED }}
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{
                                background:
                                  kwOffline || liveReadingUnavailable
                                    ? FAINT
                                    : displayHeld
                                      ? GOLD
                                      : BRIGHT_TEAL,
                              }}
                            />
                            {heroStateLabel}
                          </p>
                          <p
                            className="mt-1 text-[30px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                            style={{ fontFamily: DISPLAY }}
                          >
                            {heroKw}
                            <span className="text-[14px]" style={{ color: MUTED }}>
                              {" "}
                              kW
                            </span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[12px] font-medium" style={{ color: MUTED }}>
                            Today
                          </p>
                          <p
                            className="mt-1 text-[30px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                            style={{ fontFamily: DISPLAY }}
                          >
                            {day.kwh}
                            <span className="text-[14px]" style={{ color: MUTED }}>
                              {" "}
                              kWh
                            </span>
                          </p>
                        </div>
                      </div>
                      <div
                        className="mt-3.5 h-1.5 overflow-hidden rounded-full"
                        style={{ background: PALE_TEAL }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${heroPct}%`,
                            background: `linear-gradient(90deg, ${GOLD}, ${DEEP_GOLD})`,
                          }}
                        />
                      </div>
                      <div className="mt-2 flex items-baseline justify-between">
                        <p className="text-[11px]" style={{ color: FAINT }}>
                          {kwOffline
                            ? summary?.as_of
                              ? `last seen ${fmtClockIso(summary.as_of)}`
                              : "no readings today"
                            : liveReadingUnavailable
                              ? "no live reading available"
                            : displayHeld && summary?.as_of
                              ? `${heroPct}% of your ${systemKwp} kW system · as of ${fmtClockIso(summary.as_of)}`
                              : `${heroPct}% of your ${systemKwp} kW system`}
                        </p>
                        <p
                          className="text-[13px] font-semibold [font-variant-numeric:tabular-nums]"
                          style={{ color: TEAL }}
                        >
                          {fmtRs(day.savedRs)} saved
                        </p>
                      </div>
                    </Card>
                  )}

                  {/* Performance ratio (live only, today only) - "is my plant OK today?" */}
                  {isToday && pr && (
                    <Card testId="pr-card">
                      <div className="flex items-center justify-between">
                        <Label>Today's performance</Label>
                        <span
                          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{ background: prBand.bg, color: prBand.accent }}
                        >
                          {pr.label}
                        </span>
                      </div>
                      <p
                        data-testid="pr-ratio"
                        className="mt-1 text-[30px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                        style={{ fontFamily: DISPLAY, color: prBand.accent }}
                      >
                        {pr.ratioPct}
                        <span className="text-[14px]" style={{ color: MUTED }}>
                          {" "}
                          %
                        </span>
                      </p>
                      <p
                        className="text-[13px] [font-variant-numeric:tabular-nums]"
                        style={{ color: INK }}
                      >
                        Today: {pr.todayKwhActual} kWh vs ≈{pr.todayKwhExpected} kWh expected
                      </p>
                      <p
                        data-testid="pr-disclaimer"
                        className="mt-1.5 text-[11px] leading-snug"
                        style={{ color: FAINT }}
                      >
                        {pr.disclaimer}
                      </p>
                    </Card>
                  )}

                  {isToday && !pr && (
                    <Card testId={MONITORING.todayPerformanceUnavailable}>
                      <Label>Today's performance</Label>
                      <p className="mt-2 text-[14px] font-medium" style={{ color: INK }}>
                        Comparison unavailable
                      </p>
                      <p className="mt-1 text-[12px] leading-snug" style={{ color: MUTED }}>
                        {forecastUnavailableCopy}
                      </p>
                    </Card>
                  )}


                  {/* Cleaning nudge (only on today, only when triggered) */}
                  {isToday && cleaningNudge && (
                    <div
                      data-testid="cleaning-nudge"
                      className="flex items-start gap-3 rounded-[18px] p-5 shadow-[0_2px_14px_rgba(13,32,29,0.05)] lg:col-span-3"
                      style={{ background: PALE_TEAL }}
                    >
                      <Droplets className="mt-0.5 h-5 w-5 shrink-0" style={{ color: TEAL }} />
                      <div>
                        <p
                          className="text-[14px] font-semibold leading-snug [font-variant-numeric:tabular-nums]"
                          style={{ color: INK }}
                        >
                          Your plant is producing less than usual for the weather it's been
                          getting - a panel cleaning may help.
                        </p>
                        <p className="mt-1 text-[12px]" style={{ color: FAINT }}>
                          Dust can cost 15–25% output in Indian conditions.
                        </p>
                      </div>
                    </div>
                  )}

                  <Card className="lg:col-span-3">
                    <div className="flex items-baseline justify-between">
                      <Label>Power through the day</Label>
                      <p className="text-[12px]" style={{ color: MUTED }}>
                        Peak {day.peakKw} kW · {day.peakTime}
                      </p>
                    </div>
                    <p data-testid={MONITORING.chartUpdated} className="mt-1 text-[11px]" style={{ color: FAINT }}>
                      {updatedLabel(day?.as_of ? day : summary)}
                    </p>
                    <DayCurveChart
                      curve={day.curve}
                      expectedCurve={day.expectedCurve}
                      curveHiRes={day.curveHiRes}
                      nowH={isToday ? nowHour : null}
                    />
                  </Card>

                  <div className="grid grid-cols-1 gap-4 min-[400px]:grid-cols-2 lg:col-span-3 lg:grid-cols-3">
                    {/* Tomorrow forecast (only on today, first in the stat row) */}
                    {isToday && forecast && (
                      <Card testId="forecast-card" className="flex items-center gap-4 min-[400px]:col-span-2 lg:col-span-1">
                        <div
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                          style={{ background: PALE_GOLD, color: GOLD }}
                        >
                          {forecast.condition === "Sunny" ? (
                            <Sun className="h-5 w-5" />
                          ) : forecast.condition === "Partly cloudy" ? (
                            <CloudSun className="h-5 w-5" />
                          ) : forecast.condition === "Rain" ? (
                            <CloudRain className="h-5 w-5" />
                          ) : (
                            <Cloud className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p
                            className="text-[15px] font-medium [font-variant-numeric:tabular-nums]"
                            style={{ color: INK }}
                          >
                            Tomorrow: {forecast.condition} - expect ≈{forecast.expectedKwh} kWh
                          </p>
                          <p className="text-[12px]" style={{ color: MUTED }}>
                            {forecast.note}
                          </p>
                        </div>
                      </Card>
                    )}
                    {isToday && !forecast && !MONITORING_MOCK && (
                      <Card testId="forecast-unavailable" className="col-span-2 lg:col-span-1">
                        <p className="text-[15px] font-medium" style={{ color: INK }}>
                          Estimated generation unavailable
                        </p>
                        <p className="mt-1 text-[12px]" style={{ color: MUTED }}>
                          {forecastUnavailableCopy}
                        </p>
                      </Card>
                    )}
                    {!isToday && (
                      <>
                        <StatCard
                          label="Energy"
                          value={`${day.kwh}`}
                          unit="kWh"
                          note={`${day.sunHours} peak sun hours`}
                        />
                        <StatCard
                          label="Saved"
                          value={fmtRs(day.savedRs)}
                          note={`at ${fmtRs(tariff)} / kWh`}
                          valueColor={TEAL}
                        />
                      </>
                    )}
                    <StatCard
                      label="CO₂ avoided"
                      value={`${day.co2Kg}`}
                      unit="kg"
                      note="vs grid electricity"
                      icon={
                        <div className="flex items-center gap-2">
                          <Leaf className="h-4 w-4" style={{ color: TEAL }} />
                          <ImpactInfoButton />
                        </div>
                      }
                    />
                    <StatCard
                      label="Trees equivalent"
                      value={`${(day.co2Kg / KG_CO2_PER_TREE).toFixed(1)}`}
                      note="trees equivalent this day"
                      icon={
                        <div className="flex items-center gap-2">
                          <TreePine className="h-4 w-4" style={{ color: TEAL }} />
                          <ImpactInfoButton />
                        </div>
                      }
                    />
                  </div>
                </div>
              )}

              {/* ── MONTH VIEW · trends ────────────────────────────── */}
              {mode === "month" && month && (
                <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
                  {/* Totals + daily bars */}
                  <Card className="lg:col-span-3">
                    <div className="flex items-baseline justify-between">
                      <Label>Energy this month</Label>
                      <p
                        className="text-[13px] font-semibold [font-variant-numeric:tabular-nums]"
                        style={{ color: TEAL }}
                      >
                        {fmtRs(month.savedRs)} saved
                      </p>
                    </div>
                    <p data-testid={MONITORING.chartUpdated} className="mt-1 text-[11px]" style={{ color: FAINT }}>
                      {updatedLabel(month?.as_of ? month : summary)}
                    </p>
                    <p
                      className="mt-1 text-[34px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                      style={{ fontFamily: DISPLAY }}
                    >
                      {month.kwh.toLocaleString("en-IN")}{" "}
                      <span className="text-[16px]" style={{ color: MUTED }}>
                        kWh
                      </span>
                    </p>
                    <Bars
                      values={month.days.map((d) => d.kwh)}
                      highlight={month.bestDay.day - 1}
                    />
                  </Card>

                  <div className="grid grid-cols-1 gap-4 min-[400px]:grid-cols-2 lg:col-span-3 lg:grid-cols-3">
                    {/* Best day - the month's headline, so it gets the gold */}
                    <Card testId="best-day-card">
                      <div className="flex items-center justify-between">
                        <Label>Best day</Label>
                        <Sun className="h-4 w-4" style={{ color: GOLD }} />
                      </div>
                      <p
                        className="mt-1 text-[28px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                        style={{ fontFamily: DISPLAY, color: DEEP_GOLD }}
                      >
                        {month.bestDay.kwh}
                        <span className="text-[14px]" style={{ color: MUTED }}>
                          {" "}
                          kWh
                        </span>
                      </p>
                      <p className="text-[12px]" style={{ color: FAINT }}>
                        {month.bestDay.day} {MONTH_SHORT[anchor.getMonth()]} · top of the month
                      </p>
                    </Card>
                    {worstDay && (
                      <StatCard
                        testId="worst-day-card"
                        label="Worst day"
                        value={`${worstDay.kwh}`}
                        unit="kWh"
                        note={`${worstDay.day} ${MONTH_SHORT[anchor.getMonth()]}`}
                      />
                    )}
                    <StatCard
                      label="Daily average"
                      value={`${Math.round((month.kwh / Math.max(month.days.length, 1)) * 10) / 10}`}
                      unit="kWh"
                      note={`across ${month.days.length} days`}
                    />
                  </div>

                  {/* Trend vs the previous month (a second useMonthData query
                      on a shifted anchor - no new endpoint). Hidden right
                      after install, when there is nothing to compare with. */}
                  {hasPrevMonth && prevMonth && prevMonth.kwh > 0 && (
                    <Card testId="month-comparison" className="lg:col-span-3">
                      <div className="flex items-baseline justify-between">
                        <Label>vs {MONTH_LONG[prevAnchor.getMonth()]}</Label>
                        <span
                          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={
                            month.kwh >= prevMonth.kwh
                              ? { background: PALE_TEAL, color: TEAL }
                              : { background: PALE_GOLD, color: DEEP_GOLD }
                          }
                        >
                          {month.kwh >= prevMonth.kwh ? "▲" : "▼"}{" "}
                          {Math.abs(
                            Math.round(((month.kwh - prevMonth.kwh) / prevMonth.kwh) * 100)
                          )}
                          %
                        </span>
                      </div>
                      <p
                        className="mt-1 text-[26px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                        style={{
                          fontFamily: DISPLAY,
                          color: month.kwh >= prevMonth.kwh ? TEAL : DEEP_GOLD,
                        }}
                      >
                        {month.kwh >= prevMonth.kwh ? "+" : "−"}
                        {Math.abs(
                          Math.round(((month.kwh - prevMonth.kwh) / prevMonth.kwh) * 100)
                        )}
                        % generation
                      </p>
                      <p
                        className="text-[12px] [font-variant-numeric:tabular-nums]"
                        style={{ color: FAINT }}
                      >
                        {month.kwh.toLocaleString("en-IN")} vs{" "}
                        {prevMonth.kwh.toLocaleString("en-IN")} kWh ·{" "}
                        {fmtRs(Math.abs(month.savedRs - prevMonth.savedRs))}{" "}
                        {month.savedRs >= prevMonth.savedRs ? "more" : "less"} saved
                      </p>
                    </Card>
                  )}

                  {/* Shareable monthly summary card */}
                  <button
                    data-testid="share-summary-button"
                    onClick={handleShare}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-[12px] text-[14px] font-semibold active:scale-[0.98] transition-transform duration-150 lg:col-span-3"
                    style={{
                      background: "rgba(201,164,96,0.16)",
                      border: "1px solid rgba(138,104,38,0.25)",
                      color: INK,
                    }}
                  >
                    <Share2 className="h-4 w-4" />
                    Share monthly summary
                  </button>
                </div>
              )}

              {/* ── YEAR VIEW · money ──────────────────────────────── */}
              {mode === "year" && year && (
                <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
                  {/* Savings lead the year view; kWh is the supporting act */}
                  <Card testId="year-savings-card" className="lg:col-span-3">
                    <div className="flex items-baseline justify-between">
                      <Label>Savings this year</Label>
                      <p
                        className="text-[13px] font-semibold [font-variant-numeric:tabular-nums]"
                        style={{ color: MUTED }}
                      >
                        {year.kwh.toLocaleString("en-IN")} kWh generated
                      </p>
                    </div>
                    <p data-testid={MONITORING.chartUpdated} className="mt-1 text-[11px]" style={{ color: FAINT }}>
                      {updatedLabel(year?.as_of ? year : summary)}
                    </p>
                    <p
                      className="mt-1 text-[34px] font-medium tracking-tight [font-variant-numeric:tabular-nums]"
                      style={{ fontFamily: DISPLAY, color: DEEP_GOLD }}
                    >
                      {fmtRs(year.savedRs)}
                    </p>
                    <Bars
                      values={year.months.map((m) => m.kwh)}
                      highlight={year.bestMonth.month}
                      labels={year.months.map((m) => MONTH_SHORT[m.month])}
                    />
                  </Card>

                  <div className="grid grid-cols-1 gap-4 min-[400px]:grid-cols-2 lg:col-span-3 lg:grid-cols-3">
                    <StatCard
                      testId="best-month-card"
                      label="Best month"
                      value={year.bestMonth.kwh.toLocaleString("en-IN")}
                      unit="kWh"
                      note={`${MONTH_LONG[year.bestMonth.month]} · ≈ ${fmtRs(
                        Math.round(year.bestMonth.kwh * tariff)
                      )}`}
                      valueColor={DEEP_GOLD}
                    />
                    <StatCard
                      testId="lifetime-co2-card"
                      label="Lifetime CO₂ avoided"
                      value={`${(lifetimeCo2Kg / 1000).toFixed(1)}`}
                      unit="t"
                      note={`≈ ${Math.round(lifetimeCo2Kg / KG_CO2_PER_TREE)} trees since install`}
                      icon={
                        <div className="flex items-center gap-2">
                          <Leaf className="h-4 w-4" style={{ color: TEAL }} />
                          <ImpactInfoButton />
                        </div>
                      }
                    />
                    <PaybackCard payback={payback} systemCostRs={systemCostRs} />
                  </div>
                </div>
              )}

              {/* Bottom cards - the live-ops set (performance, lifetime strip,
                  payback, technical details) belongs to the day view; the
                  referral hook and freshness line close every view. */}
              <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
                {/* Weather-adjusted performance + tooltip */}
                {mode === "day" && (
                <Card className="relative">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Label>Plant performance</Label>
                      <button
                        data-testid="performance-info-button"
                        onClick={() => setTipOpen(!tipOpen)}
                        className="flex h-4 w-4 items-center justify-center rounded-full border text-[11px] font-bold"
                        style={{ borderColor: FAINT, color: FAINT }}
                        aria-label="About the performance comparison"
                      >
                        i
                      </button>
                    </div>
                    {perfState && perfState !== "usual" && (
                      <span
                        className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                        style={
                          perfState === "better"
                            ? { background: PALE_TEAL, color: TEAL }
                            : { background: PALE_GOLD, color: DEEP_GOLD }
                        }
                      >
                        {perfState === "better" ? "Above usual" : "Below usual"}
                      </span>
                    )}
                  </div>
                  {perfState ? (
                    <>
                      <p
                        data-testid="performance-delta"
                        className="mt-1 text-[26px] font-medium tracking-tight"
                        style={{
                          fontFamily: DISPLAY,
                          color:
                            perfState === "better"
                              ? TEAL
                              : perfState === "worse"
                                ? DEEP_GOLD
                                : INK,
                        }}
                      >
                        {perfState === "better"
                          ? "Better than usual"
                          : perfState === "worse"
                            ? "Worse than usual"
                            : "Performing as usual"}
                      </p>
                      {performance?.ratioPct != null && (
                        <p
                          data-testid="performance-ratio"
                          className="text-[13px] [font-variant-numeric:tabular-nums]"
                          style={{ color: MUTED }}
                        >
                          {performance.ratioPct}% of your plant's usual output for this weather
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-2 text-[14px] leading-snug" style={{ color: MUTED }}>
                      {isToday
                        ? "Not enough history yet - once this month has more generating days, we'll compare today against your usual."
                        : "Back on today, we'll show how your generation compares against your usual."}
                    </p>
                  )}
                  {tipOpen && (
                    <div
                      className="absolute left-4 right-4 top-12 z-10 rounded-[14px] border bg-white p-4 shadow-[0_8px_30px_rgba(13,32,29,0.12)]"
                      style={{ borderColor: BORDER }}
                    >
                      <p className="text-[13px] font-semibold mb-1" style={{ fontFamily: DISPLAY }}>
                        How this is calculated
                      </p>
                      <p className="text-[12px] leading-relaxed" style={{ color: MUTED }}>
                        We compare your plant's output against what the actual weather predicts it
                        should produce, over the last 30 days - so a cloudy week never counts
                        against your plant.
                      </p>
                      <button
                        onClick={() => setTipOpen(false)}
                        className="mt-2 text-[12px] font-semibold"
                        style={{ color: DEEP_GOLD }}
                      >
                        Got it
                      </button>
                    </div>
                  )}
                </Card>
                )}

                {/* Lifetime strip */}
                {mode === "day" && (
                <Card className="relative p-0">
                  <div className="absolute right-4 top-4">
                    <ImpactInfoButton />
                  </div>
                  <div className="px-5 py-4 text-center" style={{ background: "#f7f5ee" }}>
                    <p className="text-[12px]" style={{ color: MUTED }}>
                      Since installation ·{" "}
                      {installDateObj ? fmtDate(installDateObj) : "Date not reported"}
                    </p>
                    <p
                      className="text-[19px] font-semibold tracking-tight [font-variant-numeric:tabular-nums]"
                      style={{ fontFamily: DISPLAY }}
                    >
                      {(lifetimeKwh / 1000).toFixed(1)} MWh ·{" "}
                      <span style={{ color: DEEP_GOLD }}>
                        {fmtRs(Math.round(lifetimeKwh * tariff))} saved
                      </span>
                    </p>
                    <p
                      className="text-[11px] mt-0.5"
                      style={{ color: MUTED }}
                    >
                      {lifetimeSource === "vendor"
                        ? "From the inverter's own counter"
                        : "Total of imported days"}
                    </p>
                    <p
                      className="text-[12px] mt-0.5 [font-variant-numeric:tabular-nums]"
                      style={{ color: FAINT }}
                    >
                      {(lifetimeCo2Kg / 1000).toFixed(1)} t CO₂ · ≈ {Math.round(lifetimeCo2Kg / KG_CO2_PER_TREE)}{" "}
                      trees
                    </p>
                  </div>
                </Card>
                )}

                {/* Investment payback tracker */}
                {mode === "day" && (
                  <PaybackCard payback={payback} systemCostRs={systemCostRs} />
                )}

                {/* Technical details */}
                {mode === "day" && day && (
                  <Card testId={MONITORING.technicalDetails}>
                    <Label>Technical details</Label>
                    <div className="mt-3 space-y-2.5">
                      {[
                        [
                          "Inverter",
                          [summary?.inverterBrand, summary?.inverterModel]
                            .filter(Boolean)
                            .join(" ") || "Not reported",
                        ],
                        ["Datalogger", summary?.dataloggerPn || "Not reported"],
                        [
                          "Vendor capacity",
                          summary?.vendorCapacityKw != null
                            ? `${summary.vendorCapacityKw} kW`
                            : "Not reported",
                        ],
                        ["Plant status", summary?.plantStatus || "Not reported"],
                        [
                          "Specific yield",
                          systemKwp > 0
                            ? `${(day.kwh / systemKwp).toFixed(1)} kWh / kWp`
                            : "Not reported",
                        ],
                        [
                          "Last updated",
                          updatedLabel(summary).replace(/^Last updated /, ""),
                        ],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between gap-4">
                          <span className="text-[13px]" style={{ color: MUTED }}>
                            {k}{" "}
                          </span>
                          <span className="text-right text-[13px] font-medium" style={{ color: INK }}>
                            {v}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Referral hook */}
                <div
                  className="relative rounded-[18px] p-5 text-center shadow-[0_2px_14px_rgba(13,32,29,0.06)] lg:col-span-2"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(220,192,134,0.85), rgba(201,164,96,0.55), rgba(138,104,38,0.35))",
                  }}
                >
                  <div className="absolute right-4 top-4">
                    <ImpactInfoButton />
                  </div>
                  <p
                    className="text-[15px] font-semibold"
                    style={{ fontFamily: DISPLAY, color: "#3d2f10" }}
                  >
                    You've saved {fmtRs(Math.round(lifetimeKwh * tariff))} and your impact equals{" "}
                    {Math.round(lifetimeCo2Kg / KG_CO2_PER_TREE)} trees equivalent
                  </p>
                  {/* Referral entry is a member action; the admin shell has no /refer route. */}
                  {!isAdmin && (
                    <>
                      <p className="mt-1 text-[13px]" style={{ color: "#5c4a1e" }}>
                        Know a neighbour who'd love numbers like these?
                      </p>
                      <button
                        data-testid={MONITORING.referEarnButton}
                        onClick={() => navigate("/refer")}
                        className="mt-3 rounded-full px-6 py-2.5 text-[14px] font-semibold text-white"
                        style={{ background: INK }}
                      >
                        Refer & Earn
                      </button>
                    </>
                  )}
                </div>

                <p
                  className="px-1 pt-1 text-center text-[12px] lg:col-span-2"
                  style={{ color: FAINT }}
                >
                  {updatedLabel(summary)} ·{" "}
                  {systemKwp > 0 ? `${systemKwp} kW rooftop` : "Capacity not reported"} · Greentek India
                </p>
              </div>
              </div>
            </>
          )
        ) : (
          <SettingsPanel
            tariff={tariff}
            onTariffChange={updateTariff}
            readOnly={isViewer}
            systemInfo={{
              systemKwp,
              installDate,
              location: MONITORING_MOCK && city === "Rajkot" ? "Rajkot, Gujarat" : city || null,
              inverter: summary?.inverterModel ?? null,
              systemCostRs: summary?.systemCostRs ?? null,
              plantId,
              monitoring: MONITORING_MOCK ? "Demo data · mock source" : "ShineMonitor · live",
            }}
          />
        )}
      </div>

      {pickerOpen && (
        <PeriodPicker
          mode={mode}
          anchor={anchor}
          onSelect={(d) => goTo(mode, d)}
          onClose={() => setPickerOpen(false)}
          installDate={installDate}
        />
      )}
    </div>
  );
}

export default MonitoringSection;
